import React, { useEffect, useState } from "react";
import { Download, BookOpen, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const FreebieCard = ({ freebie, onClaim }) => {
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [loading, setLoading] = useState(false);
    const [unlocked, setUnlocked] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);

    useEffect(() => {
        // Persist unlock state per freebie
        const saved = localStorage.getItem(`mw_freebie_${freebie.id}`);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                setUnlocked(true);
                setPdfUrl(parsed.pdf_url);
            } catch (_) {}
        }
    }, [freebie.id]);

    const triggerDownload = async (url, title) => {
        const filename = `${title.replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "-")}.pdf`;
        // Try the strongest path first: fetch as blob and save. This forces a real
        // "download" instead of letting the browser open the PDF inline.
        try {
            const res = await fetch(url, { mode: "cors" });
            if (!res.ok) throw new Error("fetch failed");
            const blob = await res.blob();
            const objectUrl = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objectUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => window.URL.revokeObjectURL(objectUrl), 4000);
            return;
        } catch (e) {
            // Fall back: anchor with download attribute (works same-origin / cooperative)
            try {
                const a = document.createElement("a");
                a.href = url;
                a.download = filename;
                a.target = "_blank";
                a.rel = "noopener";
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                return;
            } catch (_) {
                // Last resort: open in a new tab so the user can save manually
                window.open(url, "_blank", "noopener,noreferrer");
            }
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !firstName.trim() || !lastName.trim()) return;
        setLoading(true);
        try {
            const res = await api.post("/freebies/claim", {
                email,
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                freebie_id: freebie.id,
            });
            const data = res.data;
            setPdfUrl(data.pdf_url);
            setUnlocked(true);
            localStorage.setItem(
                `mw_freebie_${freebie.id}`,
                JSON.stringify({ pdf_url: data.pdf_url, email })
            );
            // Auto-trigger the download immediately — don't make the user hunt for a button
            triggerDownload(data.pdf_url, freebie.title);
            toast.success("Unlocked. Your PDF is opening — check your downloads.");
            onClaim?.();
        } catch (err) {
            toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not unlock. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="bg-black border border-white/15 flex flex-col group hover:border-red-700 transition-colors overflow-hidden"
            data-testid={`freebie-card-${freebie.id}`}
        >
            {freebie.thumb_url ? (
                <div
                    className="relative block bg-[#050505] border-b border-white/10 overflow-hidden"
                    data-testid={`freebie-thumb-${freebie.id}`}
                >
                    <img
                        src={freebie.thumb_url}
                        alt={`${freebie.title} preview`}
                        loading="lazy"
                        draggable={false}
                        className="w-full h-72 md:h-80 object-cover object-top select-none pointer-events-none"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent pointer-events-none" />
                    <div className="absolute top-4 left-4 bg-red-700 px-3 py-1 font-stencil text-white text-[10px] tracking-widest">
                        PREVIEW
                    </div>
                    <div className="absolute top-4 right-4 font-stencil text-red-500 text-[10px] tracking-widest bg-black/60 px-3 py-1 border border-red-700/40">
                        FREE
                    </div>
                </div>
            ) : (
                <div className="flex items-start justify-between p-8 pb-0">
                    <div className="bg-red-700 p-3">
                        <BookOpen size={28} className="text-white" strokeWidth={1.5} />
                    </div>
                    <div className="font-stencil text-red-600 text-xs tracking-widest">
                        FREE
                    </div>
                </div>
            )}

            <div className="p-8 flex flex-col flex-1">
                <h3 className="font-display text-3xl uppercase tracking-tight leading-tight">
                    {freebie.title}
                </h3>
                <p className="mt-4 text-white/65 leading-relaxed flex-1">{freebie.description}</p>

                {!unlocked ? (
                <form onSubmit={submit} className="mt-8 space-y-3" data-testid={`freebie-form-${freebie.id}`}>
                    <div className="grid grid-cols-2 gap-3">
                        <input
                            type="text"
                            required
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            placeholder="First name"
                            className="w-full px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30"
                            data-testid={`freebie-first-name-${freebie.id}`}
                            autoComplete="given-name"
                        />
                        <input
                            type="text"
                            required
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            placeholder="Last name"
                            className="w-full px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30"
                            data-testid={`freebie-last-name-${freebie.id}`}
                            autoComplete="family-name"
                        />
                    </div>
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="w-full px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30"
                        data-testid={`freebie-email-${freebie.id}`}
                        autoComplete="email"
                    />
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full btn-primary justify-center disabled:opacity-50"
                        data-testid={`freebie-submit-${freebie.id}`}
                    >
                        <Download size={16} />
                        {loading ? "Sending..." : "Get the PDF"}
                    </button>
                    <div className="text-[11px] text-white/40">
                        We'll send the PDF and occasional encouragement. Unsubscribe anytime.
                    </div>
                </form>
            ) : (
                <div className="mt-8 space-y-3">
                    <div className="flex items-center gap-2 text-green-500 text-sm">
                        <CheckCircle2 size={16} /> Unlocked
                    </div>
                    <button
                        type="button"
                        onClick={() => triggerDownload(pdfUrl, freebie.title)}
                        className="w-full btn-primary justify-center"
                        data-testid={`freebie-download-${freebie.id}`}
                    >
                        <Download size={16} /> Download PDF
                    </button>
                    <a
                        href={pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-center text-xs text-white/40 hover:text-red-600 transition-colors"
                        data-testid={`freebie-view-${freebie.id}`}
                    >
                        or open in new tab →
                    </a>
                </div>
            )}
            </div>
        </div>
    );
};

const Resources = ({ embedded = false, title = "Free Tools for the Fight" }) => {
    const [freebies, setFreebies] = useState([]);

    useEffect(() => {
        api.get("/freebies").then((r) => setFreebies(r.data)).catch(() => setFreebies([]));
    }, []);

    return (
        <section
            className={embedded ? "py-24 md:py-32 bg-black border-t border-white/10" : "py-24 md:py-32 bg-black min-h-screen"}
            data-testid={embedded ? "resources-section" : "resources-page"}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="flex items-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                    <span className="h-px w-12 bg-red-600" />
                    Free Resources
                </div>
                <h2 className={`font-display uppercase tracking-tighter leading-[0.9] ${embedded ? "text-4xl md:text-6xl" : "text-5xl md:text-7xl"}`}>
                    {title.split(" ").slice(0, -2).join(" ")}{" "}
                    <span className="text-red-600">{title.split(" ").slice(-2).join(" ")}</span>
                </h2>
                <p className="mt-6 max-w-2xl text-white/70">
                    Hand-built tools to arm your mind. Drop your email — get the PDF, no spam, just truth.
                </p>

                <div className="mt-12 grid gap-6 max-w-2xl mx-auto md:mx-0">
                    {freebies.map((f) => (
                        <FreebieCard key={f.id} freebie={f} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Resources;
